import React, { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import recallsService from '../../../../../services/recalls.service'
import { useForm } from 'react-hook-form'
import Loading from "../../../../common/Loading";

function RecallSettings() {
    const { clinicId } = useParams()
    const [settings, setSettings] = useState(null)
    const [editing, setEditing] = useState(false)
    const [loading, setLoading] = useState(true)

    useEffect(() => {
        getSettings()
    }, [])

    const getSettings = () => {
        setLoading(true)
        recallsService.getClinicSettings(clinicId)
            .then(res => {
                setSettings(res.data)
                setLoading(false)
            })
            .catch(err => {
                console.log(err)
                toast.error("Failed to load recall settings")
                setLoading(false)
            })
    }

    const handleUpdateSettings = (newSettings) => {
        setSettings(newSettings)
        setEditing(false)
    }

    if (loading) {
        return <Loading />
    }

    return (
        <div className="row">
            <div className="col-12">
                <div className="card card-padding shadow-none card-border">
                    <div className="card-title bg-white py-3 d-flex justify-content-between align-items-center border-bottom">
                        <div className="mb-0 d-flex align-items-center">
                            <i className="bi bi-bell me-2 fs-5"></i>
                            <div className="mb-0">Recall Settings</div>
                        </div>
                        <button
                            className={`btn btn-sm ${editing ? 'btn-outline-danger' : 'btn-outline-primary'}`}
                            onClick={() => setEditing(!editing)}
                        >
                            <i className={`bi ${editing ? 'bi-x-circle' : 'bi-pencil'} me-1`}></i>
                            {editing ? 'Cancel' : 'Edit'}
                        </button>
                    </div>

                    <div className="card-body">
                        {settings && (
                            editing ?
                                <RecallEdit settings={settings} updateSettings={handleUpdateSettings} setEditing={setEditing} /> :
                                <RecallView settings={settings} />
                        )}
                    </div>
                </div>
            </div>
        </div>
    )
}

const RecallView = ({ settings }) => {
    const StatusBadge = ({ isActive }) => (
        <span className={`badge small fw-bold service-status ${isActive ? 'status-completed text-success' : 'status-incomplete text-danger'} px-2 py-1`}>
            {isActive ? 'Active' : 'Inactive'}
        </span>
    )

    const MediumBadge = ({ medium }) => {
        if (!medium) return <span className="text-muted">Not set</span>

        return (
            <span className="badge bg-primary bg-opacity-10 text-primary border border-primary border-opacity-10">
                {medium}
            </span>
        )
    }

    return (
        <div className="row">
            <div className="col-md-6">
                <div className="mb-3">
                    <label className="form-label fw-semibold text-muted small mb-1">Status</label>
                    <div><StatusBadge isActive={settings.isActive} /></div>
                </div>
            </div>
            <hr/>
            <div className="col-12">
                <h6 className="mb-3 text-primary">Daily Limits</h6>
            </div>

            <div className="col-md-4">
                <div className="mb-3">
                    <label className="form-label fw-semibold text-muted small mb-1">Email Limit</label>
                    <div className="fs-5 fw-bold text-primary">{settings.emailLimitPerDay}</div>
                </div>
            </div>

            <div className="col-md-4">
                <div className="mb-3">
                    <label className="form-label fw-semibold text-muted small mb-1">SMS Limit</label>
                    <div className="fs-5 fw-bold text-primary">{settings.smsLimitPerDay}</div>
                </div>
            </div>

            <div className="col-md-4">
                <div className="mb-3">
                    <label className="form-label fw-semibold text-muted small mb-1">Call Limit</label>
                    <div className="fs-5 fw-bold text-primary">{settings.callLimitPerDay}</div>
                </div>
            </div>
            <hr/>
            <div className="col-12">
                <h6 className="mb-3 text-primary">Recall Configuration</h6>
            </div>

            <div className="col-md-6">
                <div className="mb-3">
                    <label className="form-label fw-semibold text-muted small mb-1">Second Recall</label>
                    <div className="d-flex align-items-center">
                        <StatusBadge isActive={settings.isSecondRecall} />
                        {settings.isSecondRecall && (
                            <>
                                <span className="mx-2 text-muted">•</span>
                                <span className="me-2 small">After {settings.secondRecallWaitPeriod} days</span>
                                <MediumBadge medium={settings.secondRecallMedium} />
                            </>
                        )}
                    </div>
                </div>
            </div>

            <div className="col-md-6">
                <div className="mb-3">
                    <label className="form-label fw-semibold text-muted small mb-1">Third Recall</label>
                    <div className="d-flex align-items-center">
                        <StatusBadge isActive={settings.isThirdRecall} />
                        {settings.isThirdRecall && (
                            <>
                                <span className="mx-2 text-muted">•</span>
                                <span className="me-2 small">After {settings.thirdRecallWaitPeriod} days</span>
                                <MediumBadge medium={settings.thirdRecallMedium} />
                            </>
                        )}
                    </div>
                </div>
            </div>

            <div className="col-md-6">
                <div className="mb-3">
                    <label className="form-label fw-semibold text-muted small mb-1">Fourth Recall</label>
                    <div className="d-flex align-items-center">
                        <StatusBadge isActive={settings.isFourthRecall} />
                        {settings.isFourthRecall && (
                            <>
                                <span className="mx-2 text-muted">•</span>
                                <span className="me-2 small">After {settings.fourthRecallWaitPeriod} days</span>
                                <MediumBadge medium={settings.fourthRecallMedium} />
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    )
}

const RecallEdit = ({ settings, updateSettings, setEditing }) => {
    const { register, handleSubmit, formState: { errors, isSubmitting }, watch, setValue } = useForm({
        defaultValues: {
            id: settings.id,
            clinicId: settings.clinicId,
            isActive: settings.isActive,
            emailLimitPerDay: settings.emailLimitPerDay || 100,
            smsLimitPerDay: settings.smsLimitPerDay || 100,
            callLimitPerDay: settings.callLimitPerDay || 100,
            isSecondRecall: settings.isSecondRecall,
            secondRecallMedium: settings.secondRecallMedium || "PATIENT_PREFERRED",
            secondRecallWaitPeriod: settings.secondRecallWaitPeriod || 30,
            isThirdRecall: settings.isThirdRecall,
            thirdRecallMedium: settings.thirdRecallMedium || "PATIENT_PREFERRED",
            thirdRecallWaitPeriod: settings.thirdRecallWaitPeriod || 30,
            isFourthRecall: settings.isFourthRecall,
            fourthRecallMedium: settings.fourthRecallMedium || "PATIENT_PREFERRED",
            fourthRecallWaitPeriod: settings.fourthRecallWaitPeriod || 30,
        }
    })

    const [activeTab, setActiveTab] = useState("general");

    // Watch the boolean toggles to conditionally show medium options
    const isActive = watch("isActive")
    const isSecondRecallEnabled = watch("isSecondRecall")
    const isThirdRecallEnabled = watch("isThirdRecall")
    const isFourthRecallEnabled = watch("isFourthRecall")

    const waitPeriodOptions = [30, 45, 60, 90, 120];
    const mediumOptions = [
        { value: "EMAIL", label: "Email" },
        { value: "SMS", label: "SMS" },
        { value: "CALL", label: "Phone Call" },
        { value: "PATIENT_PREFERRED", label: "Patient Preferred" }
    ];

    const onSubmit = async (data) => {
        try {
            const updateData = {
                ...data,
                secondRecallMedium: isSecondRecallEnabled ? data.secondRecallMedium : "PATIENT_PREFERRED",
                thirdRecallMedium: isThirdRecallEnabled ? data.thirdRecallMedium : "PATIENT_PREFERRED",
                fourthRecallMedium: isFourthRecallEnabled ? data.fourthRecallMedium : "PATIENT_PREFERRED"
            }

            const response = await recallsService.saveSettings(data.clinicId, updateData)
            updateSettings(response.data)

        } catch (err) {
            toast.error("Error updating settings. Please try again.")
        }
    }

    const ToggleSwitch = ({ name, checked, onChange, label }) => (
        <div className="form-check form-switch">
            <input
                className="form-check-input"
                type="checkbox"
                id={name}
                checked={checked}
                onChange={onChange}
                style={{ width: '2.5em', height: '1.25em' }}
            />
            <label className="form-check-label fw-semibold ms-2" htmlFor={name}>
                {label}
            </label>
        </div>
    );

    return (
        <form onSubmit={handleSubmit(onSubmit)}>
            <ul className="nav nav-tabs small mb-4" role="tablist">
                <li className="nav-item" role="presentation">
                    <button
                        className={`nav-link ${activeTab === "general" ? "active" : ""}`}
                        onClick={() => setActiveTab("general")}
                        type="button"
                    >
                        General
                    </button>
                </li>
                <li className="nav-item" role="presentation">
                    <button
                        className={`nav-link ${activeTab === "second" ? "active" : ""}`}
                        onClick={() => setActiveTab("second")}
                        type="button"
                    >
                        Second Recall
                    </button>
                </li>
                <li className="nav-item" role="presentation">
                    <button
                        className={`nav-link ${activeTab === "third" ? "active" : ""}`}
                        onClick={() => setActiveTab("third")}
                        type="button"
                    >
                        Third Recall
                    </button>
                </li>
                <li className="nav-item" role="presentation">
                    <button
                        className={`nav-link ${activeTab === "fourth" ? "active" : ""}`}
                        onClick={() => setActiveTab("fourth")}
                        type="button"
                    >
                        Fourth Recall
                    </button>
                </li>
            </ul>

            <div className="tab-content small">
                {/* General Settings Tab */}
                {activeTab === "general" && (
                    <div className="row">
                        <div className="col-md-6">
                            <div className="mb-4">
                                <ToggleSwitch
                                    name="isActive"
                                    checked={isActive}
                                    onChange={(e) => setValue("isActive", e.target.checked)}
                                    label="Recall System Active"
                                />
                                <div className="form-text">
                                    {isActive ? "Recall system is currently active" : "Recall system is currently inactive"}
                                </div>
                            </div>
                        </div>

                        <div className="col-12">
                            <h6 className="mb-3 text-primary">Daily Limits</h6>
                        </div>

                        <div className="col-md-4">
                            <div className="mb-3">
                                <label className="form-label fw-semibold">Email Limit</label>
                                <input
                                    type="number"
                                    className={`form-control ${errors.emailLimitPerDay ? 'is-invalid' : ''}`}
                                    {...register('emailLimitPerDay', {
                                        required: "Email limit is required",
                                        min: { value: 0, message: "Minimum value is 0" },
                                        max: { value: 500, message: "Maximum value is 500" }
                                    })}
                                />
                                {errors.emailLimitPerDay && (
                                    <div className="invalid-feedback">{errors.emailLimitPerDay.message}</div>
                                )}
                                <div className="form-text">Maximum emails per day</div>
                            </div>
                        </div>

                        <div className="col-md-4">
                            <div className="mb-3">
                                <label className="form-label fw-semibold">SMS Limit</label>
                                <input
                                    type="number"
                                    className={`form-control ${errors.smsLimitPerDay ? 'is-invalid' : ''}`}
                                    {...register('smsLimitPerDay', {
                                        required: "SMS limit is required",
                                        min: { value: 0, message: "Minimum value is 0" },
                                        max: { value: 500, message: "Maximum value is 500" }
                                    })}
                                />
                                {errors.smsLimitPerDay && (
                                    <div className="invalid-feedback">{errors.smsLimitPerDay.message}</div>
                                )}
                                <div className="form-text">Maximum SMS per day</div>
                            </div>
                        </div>

                        <div className="col-md-4">
                            <div className="mb-3">
                                <label className="form-label fw-semibold">Call Limit</label>
                                <input
                                    type="number"
                                    className={`form-control ${errors.callLimitPerDay ? 'is-invalid' : ''}`}
                                    {...register('callLimitPerDay', {
                                        required: "Call limit is required",
                                        min: { value: 0, message: "Minimum value is 0" },
                                        max: { value: 500, message: "Maximum value is 500" }
                                    })}
                                />
                                {errors.callLimitPerDay && (
                                    <div className="invalid-feedback">{errors.callLimitPerDay.message}</div>
                                )}
                                <div className="form-text">Maximum calls per day</div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Second Recall Tab */}
                {activeTab === "second" && (
                    <div className="row">
                        <div className="col-md-6">
                            <div className="mb-4">
                                <ToggleSwitch
                                    name="isSecondRecall"
                                    checked={isSecondRecallEnabled}
                                    onChange={(e) => setValue("isSecondRecall", e.target.checked)}
                                    label="Enable Second Recall"
                                />
                                <div className="form-text">
                                    {isSecondRecallEnabled ? "Second recall is enabled" : "Second recall is disabled"}
                                </div>
                            </div>
                        </div>

                        {isSecondRecallEnabled && (
                            <>
                                <div className="col-md-6">
                                    <div className="mb-3">
                                        <label className="form-label fw-semibold">Wait Period (Days)</label>
                                        <select
                                            className={`form-select ${errors.secondRecallWaitPeriod ? 'is-invalid' : ''}`}
                                            {...register('secondRecallWaitPeriod', {
                                                required: "Wait period is required",
                                            })}
                                        >
                                            {waitPeriodOptions.map(option => (
                                                <option key={option} value={option}>{option} days</option>
                                            ))}
                                        </select>
                                        {errors.secondRecallWaitPeriod && (
                                            <div className="invalid-feedback">{errors.secondRecallWaitPeriod.message}</div>
                                        )}
                                        <div className="form-text">Days after initial appointment</div>
                                    </div>
                                </div>

                                <div className="col-md-6">
                                    <div className="mb-3">
                                        <label className="form-label fw-semibold">Medium</label>
                                        <select
                                            className={`form-select ${errors.secondRecallMedium ? 'is-invalid' : ''}`}
                                            {...register('secondRecallMedium', {
                                                required: "Medium is required",
                                            })}
                                        >
                                            {mediumOptions.map(option => (
                                                <option key={option.value} value={option.value}>{option.label}</option>
                                            ))}
                                        </select>
                                        {errors.secondRecallMedium && (
                                            <div className="invalid-feedback">{errors.secondRecallMedium.message}</div>
                                        )}
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                )}

                {/* Third Recall Tab */}
                {activeTab === "third" && (
                    <div className="row">
                        <div className="col-md-6">
                            <div className="mb-4">
                                <ToggleSwitch
                                    name="isThirdRecall"
                                    checked={isThirdRecallEnabled}
                                    onChange={(e) => setValue("isThirdRecall", e.target.checked)}
                                    label="Enable Third Recall"
                                />
                                <div className="form-text">
                                    {isThirdRecallEnabled ? "Third recall is enabled" : "Third recall is disabled"}
                                </div>
                            </div>
                        </div>

                        {isThirdRecallEnabled && (
                            <>
                                <div className="col-md-6">
                                    <div className="mb-3">
                                        <label className="form-label fw-semibold">Wait Period (Days)</label>
                                        <select
                                            className={`form-select ${errors.thirdRecallWaitPeriod ? 'is-invalid' : ''}`}
                                            {...register('thirdRecallWaitPeriod', {
                                                required: "Wait period is required",
                                            })}
                                        >
                                            {waitPeriodOptions.map(option => (
                                                <option key={option} value={option}>{option} days</option>
                                            ))}
                                        </select>
                                        {errors.thirdRecallWaitPeriod && (
                                            <div className="invalid-feedback">{errors.thirdRecallWaitPeriod.message}</div>
                                        )}
                                        <div className="form-text">Days after second recall</div>
                                    </div>
                                </div>

                                <div className="col-md-6">
                                    <div className="mb-3">
                                        <label className="form-label fw-semibold">Medium</label>
                                        <select
                                            className={`form-select ${errors.thirdRecallMedium ? 'is-invalid' : ''}`}
                                            {...register('thirdRecallMedium', {
                                                required: "Medium is required",
                                            })}
                                        >
                                            {mediumOptions.map(option => (
                                                <option key={option.value} value={option.value}>{option.label}</option>
                                            ))}
                                        </select>
                                        {errors.thirdRecallMedium && (
                                            <div className="invalid-feedback">{errors.thirdRecallMedium.message}</div>
                                        )}
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                )}

                {/* Fourth Recall Tab */}
                {activeTab === "fourth" && (
                    <div className="row">
                        <div className="col-md-6">
                            <div className="mb-4">
                                <ToggleSwitch
                                    name="isFourthRecall"
                                    checked={isFourthRecallEnabled}
                                    onChange={(e) => setValue("isFourthRecall", e.target.checked)}
                                    label="Enable Fourth Recall"
                                />
                                <div className="form-text">
                                    {isFourthRecallEnabled ? "Fourth recall is enabled" : "Fourth recall is disabled"}
                                </div>
                            </div>
                        </div>

                        {isFourthRecallEnabled && (
                            <>
                                <div className="col-md-6">
                                    <div className="mb-3">
                                        <label className="form-label fw-semibold">Wait Period (Days)</label>
                                        <select
                                            className={`form-select ${errors.fourthRecallWaitPeriod ? 'is-invalid' : ''}`}
                                            {...register('fourthRecallWaitPeriod', {
                                                required: "Wait period is required",
                                            })}
                                        >
                                            {waitPeriodOptions.map(option => (
                                                <option key={option} value={option}>{option} days</option>
                                            ))}
                                        </select>
                                        {errors.fourthRecallWaitPeriod && (
                                            <div className="invalid-feedback">{errors.fourthRecallWaitPeriod.message}</div>
                                        )}
                                        <div className="form-text">Days after third recall</div>
                                    </div>
                                </div>

                                <div className="col-md-6">
                                    <div className="mb-3">
                                        <label className="form-label fw-semibold">Medium</label>
                                        <select
                                            className={`form-select ${errors.fourthRecallMedium ? 'is-invalid' : ''}`}
                                            {...register('fourthRecallMedium', {
                                                required: "Medium is required",
                                            })}
                                        >
                                            {mediumOptions.map(option => (
                                                <option key={option.value} value={option.value}>{option.label}</option>
                                            ))}
                                        </select>
                                        {errors.fourthRecallMedium && (
                                            <div className="invalid-feedback">{errors.fourthRecallMedium.message}</div>
                                        )}
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                )}
            </div>

            <div className="text-end mt-4 pt-3 border-top">
                <button
                    type="button"
                    className="btn btn-sm btn-danger me-2"
                    onClick={() => setEditing(false)}
                >
                    <i className="bi bi-x"></i> Cancel
                </button>
                <button
                    type="submit"
                    className="btn btn-sm btn-success"
                    disabled={isSubmitting}
                >
                    {isSubmitting ? (
                        <>
                            <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                            Updating...
                        </>
                    ) : (
                        <>
                            <i className="bi bi-check-circle me-2"></i>Update Settings
                        </>
                    )}
                </button>
            </div>
        </form>
    )
}

export default RecallSettings